import { useEffect, useState } from 'react';
import { MdClose, MdHourglassEmpty } from 'react-icons/md';
import styles from './Metadata.module.css';

const EMPTY_METADATA = {
  researchers: [],
  title: '',
  dateWritten: '',
  tags: [],
};

const normalizeMetadata = (metadata) => ({
  researchers: Array.isArray(metadata?.researchers) ? metadata.researchers : [],
  title: metadata?.title || '',
  dateWritten: metadata?.dateWritten || '',
  tags: Array.isArray(metadata?.tags) ? metadata.tags : [],
});

const Metadata = ({ metadata, onUpdate, onRegenerate, isLoading }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localMetadata, setLocalMetadata] = useState(normalizeMetadata(metadata || EMPTY_METADATA));

  useEffect(() => {
    setLocalMetadata(normalizeMetadata(metadata || EMPTY_METADATA));
  }, [metadata]);

  const handleAddResearcher = () => {
    setLocalMetadata((current) => ({
      ...current,
      researchers: [...current.researchers, { name: '' }],
    }));
  };

  const handleUpdateResearcher = (index, field, value) => {
    const updated = [...localMetadata.researchers];
    updated[index] = { ...updated[index], [field]: value };
    setLocalMetadata({ ...localMetadata, researchers: updated });
  };

  const handleRemoveResearcher = (index) => {
    setLocalMetadata((current) => ({
      ...current,
      researchers: current.researchers.filter((_, currentIndex) => currentIndex !== index),
    }));
  };

  const handleAddTag = (tagText) => {
    const nextTag = tagText.trim();
    if (!nextTag || localMetadata.tags.includes(nextTag)) {
      return;
    }

    setLocalMetadata((current) => ({
      ...current,
      tags: [...current.tags, nextTag],
    }));
  };

  const handleRemoveTag = (tagToRemove) => {
    setLocalMetadata((current) => ({
      ...current,
      tags: current.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleSave = () => {
    onUpdate?.(localMetadata);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setLocalMetadata(normalizeMetadata(metadata || EMPTY_METADATA));
    setIsEditing(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleAddTag(event.target.value);
      event.target.value = '';
    }
  };

  const populatedFieldCount = [
    localMetadata.title,
    localMetadata.dateWritten,
    localMetadata.researchers.length > 0,
    localMetadata.tags.length > 0,
  ].filter(Boolean).length;

  return (
    <div className={styles.metadataPanel}>
      <div className={styles.metadataActions}>
        <div className={styles.metadataStatus}>
          <span className={styles.metadataSummary}>
            {isEditing
              ? 'Editing metadata'
              : populatedFieldCount > 0
                ? `${populatedFieldCount} field${populatedFieldCount === 1 ? '' : 's'} available`
                : 'No metadata extracted yet'}
          </span>
          {isLoading && (
            <span className={styles.loadingBadge}>
              <MdHourglassEmpty />
              Extracting...
            </span>
          )}
        </div>

        {!isEditing ? (
          <div className={styles.actionButtons}>
            <button
              className={styles.secondaryButton}
              onClick={onRegenerate}
              type="button"
              disabled={isLoading}
            >
              Regenerate
            </button>
            <button
              className={styles.primaryButton}
              onClick={() => setIsEditing(true)}
              type="button"
              disabled={isLoading}
            >
              Edit
            </button>
          </div>
        ) : (
          <div className={styles.actionButtons}>
            <button
              className={styles.secondaryButton}
              onClick={handleCancel}
              type="button"
            >
              Cancel
            </button>
            <button
              className={styles.primaryButton}
              onClick={handleSave}
              type="button"
            >
              Save
            </button>
          </div>
        )}
      </div>

      <div className={styles.metadataContent}>
        <section className={styles.metadataSection}>
          <label className={styles.sectionLabel}>Title</label>
          {isEditing ? (
            <input
              type="text"
              className={styles.input}
              placeholder="Add a document title"
              value={localMetadata.title}
              onChange={(event) => setLocalMetadata({ ...localMetadata, title: event.target.value })}
            />
          ) : (
            <div className={styles.displayCard}>
              <span className={styles.displayText}>
                {localMetadata.title || <span className={styles.emptyText}>No title provided</span>}
              </span>
            </div>
          )}
        </section>

        <section className={styles.metadataSection}>
          <label className={styles.sectionLabel}>Researcher(s)</label>
          {isEditing ? (
            <div className={styles.editGroup}>
              {localMetadata.researchers.map((researcher, index) => (
                <div key={index} className={styles.researcherRow}>
                  <input
                    type="text"
                    className={styles.input}
                    placeholder="Name"
                    value={researcher.name || ''}
                    onChange={(event) => handleUpdateResearcher(index, 'name', event.target.value)}
                  />
                  <button
                    className={styles.iconButton}
                    onClick={() => handleRemoveResearcher(index)}
                    type="button"
                    aria-label={`Remove researcher ${index + 1}`}
                  >
                    <MdClose />
                  </button>
                </div>
              ))}
              <button
                className={styles.addButton}
                onClick={handleAddResearcher}
                type="button"
              >
                + Add Researcher
              </button>
            </div>
          ) : (
            <div className={styles.displayCard}>
              {localMetadata.researchers.length > 0 ? (
                <div className={styles.researchersDisplay}>
                  {localMetadata.researchers.map((researcher, index) => (
                    <span key={index} className={styles.researcherItem}>
                      {researcher.name || 'Unnamed'}
                    </span>
                  ))}
                </div>
              ) : (
                <span className={styles.emptyText}>No researchers added</span>
              )}
            </div>
          )}
        </section>

        <section className={styles.metadataSection}>
          <label className={styles.sectionLabel}>Date</label>
          {isEditing ? (
            <input
              type="date"
              className={styles.input}
              value={localMetadata.dateWritten}
              onChange={(event) => setLocalMetadata({ ...localMetadata, dateWritten: event.target.value })}
            />
          ) : (
            <div className={styles.displayCard}>
              <span className={styles.displayText}>
                {localMetadata.dateWritten || <span className={styles.emptyText}>No date provided</span>}
              </span>
            </div>
          )}
        </section>

        <section className={styles.metadataSection}>
          <label className={styles.sectionLabel}>Tags</label>
          {isEditing ? (
            <div className={styles.editGroup}>
              <div className={styles.tagsList}>
                {localMetadata.tags.map((tag, index) => (
                  <span key={index} className={styles.tag}>
                    {tag}
                    <button
                      className={styles.tagRemove}
                      onClick={() => handleRemoveTag(tag)}
                      type="button"
                      aria-label={`Remove tag ${tag}`}
                    >
                      <MdClose />
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="text"
                className={styles.input}
                placeholder="Add tag and press Enter"
                onKeyDown={handleKeyDown}
              />
            </div>
          ) : (
            <div className={styles.displayCard}>
              {localMetadata.tags.length > 0 ? (
                <div className={styles.tagsDisplay}>
                  {localMetadata.tags.map((tag, index) => (
                    <span key={index} className={styles.tagDisplay}>
                      {tag}
                    </span>
                  ))}
                </div>
              ) : (
                <span className={styles.emptyText}>No tags added</span>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Metadata;
